import {
  DataField,
  Flex,
  Header,
  InfoBadge,
  InfoBadgeTypes,
  Space,
  Spacer,
  TextLoader,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type Vendor } from '@webapp/models'
import { VendorTypes } from '@webapp/models/enums/Vendor'
import { isPendingVendorApproval } from '@webapp/utils'
import messages from './messages'
import { VendorTooltipStyled } from './styles/VendorTooltipStyled'

const VendorTooltipLoader = () => {
  const intl = useProcurifyIntl()

  return (
    <VendorTooltipStyled>
      <Space direction='horizontal' size='small' align='center'>
        <Header as='h5'>{intl.formatMessage(messages.tooltipTitle)}</Header>
      </Space>

      <Spacer />

      <Flex.Row>
        <Flex.Item width={1 / 2}>
          <DataField label={intl.formatMessage(messages.contactNameLabel)}>
            <TextLoader width={150} />
          </DataField>
        </Flex.Item>
        <Flex.Item width={1 / 2}>
          <DataField label={intl.formatMessage(messages.phoneLabel)}>
            <TextLoader width={150} />
          </DataField>
        </Flex.Item>
      </Flex.Row>

      <Flex.Row>
        <Flex.Item width={1}>
          <DataField label={intl.formatMessage(messages.emailLabel)}>
            <TextLoader width={150} />
          </DataField>
        </Flex.Item>
      </Flex.Row>
    </VendorTooltipStyled>
  )
}

export interface IVendorTooltipProps {
  vendor: Vendor
  isLoading?: boolean
  isVendorApprovalsEnabled?: boolean
}

export const VendorTooltip = ({
  vendor,
  isLoading,
  isVendorApprovalsEnabled,
}: IVendorTooltipProps) => {
  const intl = useProcurifyIntl()

  const showLoader = isLoading || !vendor

  const emptyValueText = '--'

  const isPendingVendor =
    isVendorApprovalsEnabled && isPendingVendorApproval(vendor)

  return showLoader ? (
    <VendorTooltipLoader />
  ) : (
    <VendorTooltipStyled>
      <Space direction='horizontal' size='small' align='center'>
        <Header as='h5'>{intl.formatMessage(messages.tooltipTitle)}</Header>
        {isPendingVendor && (
          <InfoBadge
            text={intl.formatMessage(messages.pendingRequestLabel)}
            type={InfoBadgeTypes.LOW_PRIORITY}
            size='small'
          />
        )}
        {!isPendingVendor && vendor.type === VendorTypes.PREFERRED && (
          <InfoBadge
            type={InfoBadgeTypes.LOW_PRIORITY}
            text={intl.formatMessage(messages.preferredVendorLabel)}
            size='small'
          />
        )}
      </Space>

      <Spacer />

      <Flex.Row>
        <Flex.Item width={1 / 2}>
          <DataField label={intl.formatMessage(messages.contactNameLabel)}>
            {vendor.contact || emptyValueText}
          </DataField>
        </Flex.Item>
        <Flex.Item width={1 / 2}>
          <DataField label={intl.formatMessage(messages.phoneLabel)}>
            {vendor.phoneOne || emptyValueText}
          </DataField>
        </Flex.Item>
      </Flex.Row>

      <Flex.Row>
        <Flex.Item width={vendor.external_id ? 1 / 2 : 1}>
          <DataField label={intl.formatMessage(messages.emailLabel)}>
            {Object.keys(vendor.email_configurations ?? {})[0] ||
              emptyValueText}
          </DataField>
        </Flex.Item>

        {vendor.external_id && (
          <Flex.Item width={1 / 2}>
            <DataField label={intl.formatMessage(messages.vendorIdLabel)}>
              {vendor.external_id}
            </DataField>
          </Flex.Item>
        )}
      </Flex.Row>
    </VendorTooltipStyled>
  )
}

VendorTooltip.displayName = 'VendorTooltip'
